import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Workflow, Check, Save, Link2Off } from 'lucide-react';
import Button from '../../common/Button';
import FormControl from '../../common/FormControl';
import Toast, { ToastMessage } from '../../common/Toast';
import {
    ProcedureTemplate, SOPProcess,
    procedureTemplateService, sopPickerService,
} from '../../../services/auditService';
import { extractApiError } from '../../../utils/apiError';

interface ProcedureWalkthroughProps {
    procedure: ProcedureTemplate;
}

const ProcedureWalkthrough: React.FC<ProcedureWalkthroughProps> = ({ procedure }) => {
    const [sops, setSops] = useState<SOPProcess[]>([]);
    const [linkedId, setLinkedId] = useState<number | null>(procedure.linked_sop_id ?? null);
    const [draftId, setDraftId] = useState<number | null>(procedure.linked_sop_id ?? null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [savedFlash, setSavedFlash] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [list, fresh] = await Promise.all([
                sopPickerService.list(),
                procedureTemplateService.get(procedure.id),
            ]);
            setSops(list);
            setLinkedId(fresh.linked_sop_id ?? null);
            setDraftId(fresh.linked_sop_id ?? null);
        } catch (e: any) {
            setError(extractApiError(e, 'Failed to load SOPs.'));
        } finally {
            setLoading(false);
        }
    }, [procedure.id]);

    useEffect(() => { reload(); }, [reload]);

    const linked = useMemo(() => sops.find(s => s.id === linkedId) || null, [sops, linkedId]);
    const dirty = draftId !== linkedId;

    const handleSave = useCallback(async () => {
        setSaving(true);
        try {
            await procedureTemplateService.update(procedure.id, { linked_sop_id: draftId });
            setLinkedId(draftId);
            setSavedFlash(true);
            setTimeout(() => setSavedFlash(false), 1500);
            setToast({ type: 'success', message: 'Selection saved.' });
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Save failed.') });
        } finally {
            setSaving(false);
        }
    }, [procedure.id, draftId]);

    if (loading) {
        return (
            <div className="p-12 flex items-center justify-center text-gray-400 h-full">
                <Loader2 className="animate-spin mr-2" size={18} /> Loading SOPs…
            </div>
        );
    }
    if (error) return <div className="p-6 text-red-600 text-sm">{error}</div>;

    return (
        <div className="h-full flex flex-col bg-white">
            <div className="flex justify-end items-center gap-2 px-4 py-2 border-b bg-white">
                {savedFlash && <span className="text-xs text-green-700 bg-green-50 border border-green-100 px-2 py-1 rounded inline-flex items-center gap-1"><Check size={12} /> Saved</span>}
                <Button variant="fill" icon={Save} data-testid="BTN_walkthrough_template_save" onClick={handleSave} disabled={!dirty || saving}>
                    {saving ? 'Saving…' : 'Save Selection'}
                </Button>
            </div>
            <div className="flex-1 overflow-auto p-6">
            <div className="max-w-2xl mx-auto space-y-6">
                <div>
                    <h4 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                        <Workflow size={18} className="text-blue-600" /> Process Walkthrough
                    </h4>
                    <p className="text-sm text-gray-500 mt-1">
                        A walkthrough exercises an existing governance SOP. Pick the SOP whose steps the auditor will follow.
                    </p>
                </div>

                <div>
                    {sops.length === 0 ? (
                        <>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">SOP / Process</label>
                            <div className="text-sm text-gray-500 italic border border-gray-200 rounded p-3 bg-gray-50">
                                No SOPs defined yet. Create one under Governance → Processes first.
                            </div>
                        </>
                    ) : (
                        <FormControl.SearchSelect
                            label="SOP / Process"
                            data-testid="SEL_walkthrough_template_sop"
                            options={sops.map(s => ({
                                value: String(s.id),
                                label: `${s.process_name}${s.process_status ? ` (${s.process_status})` : ''}`,
                            }))}
                            value={draftId != null ? String(draftId) : ''}
                            onChange={v => setDraftId(v ? Number(v) : null)}
                            placeholder="— None —"
                            clearable
                            helperText='Click "Save Selection" above after picking. Clearing the field removes the link.'
                        />
                    )}
                </div>

                {linked && (
                    <div className="border border-blue-100 bg-blue-50/40 rounded-lg p-4">
                        <div className="text-xs uppercase tracking-wider text-blue-700 font-semibold mb-1">Currently linked</div>
                        <div className="font-semibold text-gray-800">{linked.process_name}</div>
                        {linked.description && <div className="text-sm text-gray-600 mt-1">{linked.description}</div>}
                        <div className="mt-2">
                            <Button variant="delete-outline" icon={Link2Off} data-testid="BTN_walkthrough_template_clear_link" onClick={() => setDraftId(null)}>
                                Clear link
                            </Button>
                        </div>
                    </div>
                )}
            </div>
            </div>
            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default ProcedureWalkthrough;
